/**
 * The Action Theater contract.
 *
 * Every operation the bank performs — an MCP tool call, a shell command, a
 * pipeline step — produces one Trace. The server emits Trace steps over SSE as
 * they happen; the client renders them as a live stepper. Both sides share this
 * file so a step can never be emitted in a shape the UI cannot draw.
 */

export type TraceStageId =
  | 'composed'
  | 'canonicalized'
  | 'offline_precheck'
  | 'request'
  | 'shield'
  | 'policy'
  | 'decision'
  | 'approval'
  | 'execution'
  | 'response_scan'
  | 'evidence';

export type TraceStepStatus = 'pending' | 'running' | 'waiting' | 'done' | 'skipped' | 'failed';

/** The engine's verdict vocabulary. Mirrors ActionPolicyVerdict. */
export type PolicyVerdict = 'allow' | 'block' | 'require_approval' | 'log';

/** The runtime API's decision vocabulary. */
export type RuntimeDecision = 'allow' | 'block' | 'approval' | 'mask';

export type TraceOutcome = 'allowed' | 'blocked' | 'awaiting_approval' | 'approved' | 'rejected' | 'error';

// --------------------------------------------------------------- step detail

export interface ComposedDetail {
  kind: 'composed';
  operatorName: string;
  operatorRole: string;
  developerName: string;
  hostname: string;
  hostNickname: string;
  ipAddress: string;
  agentName: string;
  toolName: string;
  mcpServer?: string;
  cwd?: string;
  args: Record<string, unknown>;
}

export interface CanonicalizedDetail {
  kind: 'canonicalized';
  raw: string;
  canonical: string;
  /** Human-readable list of what was undone, e.g. "decoded base64 segment". */
  transformations: string[];
  /** True when canonicalisation actually changed the text. */
  changed: boolean;
}

export interface OfflinePrecheckDetail {
  kind: 'offline_precheck';
  /** Policies the locally cached bundle contained at evaluation time. */
  policiesConsidered: Array<{ id: string; name: string; stage: string; ruleCount: number }>;
  verdict: PolicyVerdict;
  matchedPolicyName?: string;
  matchedRule?: string;
  /** Monitor-stage policies that would have matched without enforcing. */
  monitorMatches: Array<{ policyName: string; verdict: PolicyVerdict; matchedRule: string }>;
  /** The operation the engine inferred from the call, before any policy ran. */
  inferredOperation: string;
  policyHash: string;
  note: string;
}

export interface HttpExchangeDetail {
  kind: 'request';
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody: unknown;
  responseStatus?: number;
  responseBody?: unknown;
  durationMs?: number;
  error?: string;
  /** Set when the control plane was unreachable and the offline verdict stood. */
  offlineFallback?: boolean;
}

export type ShieldTierVerdict = 'pass' | 'flag' | 'block' | 'skipped' | 'deciding';

export interface ShieldTier {
  id: string;
  label: string;
  description: string;
  verdict: ShieldTierVerdict;
  latencyMs?: number;
  /** Free-form per-tier readout, e.g. classifier scores. */
  readout?: Array<{ label: string; value: string }>;
  note?: string;
}

export interface ShieldDetail {
  kind: 'shield';
  scanned: string;
  tiers: ShieldTier[];
  decidingTierId?: string;
  analysisPath?: string;
  confidence?: number;
  blocked: boolean;
  reason?: string;
  totalLatencyMs?: number;
}

export interface PolicyDetail {
  kind: 'policy';
  verdict: PolicyVerdict;
  policyName?: string;
  matchedRule?: string;
  reason?: string;
  monitorMatches: Array<{ policyName: string; verdict: PolicyVerdict; matchedRule: string }>;
  /** Whether the server verdict agreed with the offline pre-check. */
  agreesWithOffline: boolean;
}

export interface DecisionDetail {
  kind: 'decision';
  decision: RuntimeDecision;
  reason: string;
  /** What a legitimate operator should do instead, when the action was refused. */
  guidance?: string;
}

export interface ApprovalDetail {
  kind: 'approval';
  actionId?: string;
  routedTo: string[];
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  resolvedBy?: string;
  resolvedAt?: number;
  note?: string;
}

export interface TerminalLine {
  stream: 'stdout' | 'stderr' | 'meta';
  text: string;
  at: number;
}

export interface ExecutionDetail {
  kind: 'execution';
  hostId: string;
  hostname: string;
  shellUser: string;
  cwd: string;
  command?: string;
  lines: TerminalLine[];
  exitCode?: number;
  /** Present for MCP tool calls rather than shell. */
  result?: unknown;
}

export interface ResponseScanDetail {
  kind: 'response_scan';
  scannedBytes: number;
  flagged: boolean;
  findings: Array<{ label: string; severity: 'low' | 'medium' | 'high' | 'critical'; detail: string }>;
  reason?: string;
  /** The sanitised content actually returned to the model, when it differed. */
  sanitizedPreview?: string;
}

export interface EvidenceDetail {
  kind: 'evidence';
  eventId?: string;
  actionId?: string;
  dashboardUrl?: string;
  receipt?: string;
  recordedSurfaces: string[];
}

export type TraceStepDetail =
  | ComposedDetail
  | CanonicalizedDetail
  | OfflinePrecheckDetail
  | HttpExchangeDetail
  | ShieldDetail
  | PolicyDetail
  | DecisionDetail
  | ApprovalDetail
  | ExecutionDetail
  | ResponseScanDetail
  | EvidenceDetail;

export interface TraceStep {
  id: TraceStageId;
  title: string;
  /** One line the operator can read without expanding anything. */
  summary: string;
  status: TraceStepStatus;
  startedAt?: number;
  endedAt?: number;
  durationMs?: number;
  detail?: TraceStepDetail;
}

// -------------------------------------------------------------------- trace

/**
 * The plain-language wrapper around a trace.
 *
 * A demo is only as good as the sentence the audience can repeat afterwards.
 * Every trace therefore ends with a headline, an explanation in ordinary English
 * with no product vocabulary in it, and a one-line statement of what the
 * audience just watched being proved.
 */
export interface TraceNarration {
  headline: string;
  plain: string;
  proves: string;
  /** Where in AgentGuard to go and see the same event from the other side. */
  seeItIn?: string;
}

export interface TraceSummary {
  id: string;
  /** Short human label, e.g. "Create wire transfer". */
  title: string;
  domain: string;
  operatorId: string;
  operatorName: string;
  hostId: string;
  agentName: string;
  toolName: string;
  startedAt: number;
  endedAt?: number;
  outcome?: TraceOutcome;
  narration?: TraceNarration;
  /** Scenario that triggered it, when the operator used a guided scenario. */
  scenarioId?: string;
}

export interface Trace extends TraceSummary {
  steps: TraceStep[];
  /** What AgentGuard says about this action when asked afterwards. */
  mirror?: MirrorRecord;
}

// --------------------------------------------------------------- sse events

export interface MirrorRecord {
  /** What AgentGuard reports back about this action, read live from the API. */
  fetchedAt: number;
  found: boolean;
  decision?: string;
  policyName?: string;
  reason?: string;
  surfaces: string[];
  dashboardUrl?: string;
  error?: string;
}

export type TraceEvent =
  /** Carries the full stage ladder, so the console can show what is about to be checked. */
  | { type: 'trace.started'; trace: Trace }
  | { type: 'trace.step'; traceId: string; step: TraceStep }
  | { type: 'trace.line'; traceId: string; line: TerminalLine }
  | { type: 'trace.completed'; traceId: string; outcome: TraceOutcome; endedAt: number; narration: TraceNarration }
  | { type: 'trace.mirror'; traceId: string; mirror: MirrorRecord }
  | { type: 'approval.updated'; traceId: string; status: ApprovalDetail['status']; resolvedBy?: string }
  | { type: 'fleet.tick'; online: number; total: number; at: number }
  | { type: 'hello'; sessionId: string; at: number };

/** Ordered stage catalogue, so the UI can render the full ladder up front. */
export const TRACE_STAGES: Array<{ id: TraceStageId; title: string; explains: string }> = [
  {
    id: 'composed',
    title: 'Composed',
    explains: 'Who issued the action, from which host, with what arguments.',
  },
  {
    id: 'canonicalized',
    title: 'Canonicalized',
    explains: 'The command as written, beside what it actually resolves to once obfuscation is undone.',
  },
  {
    id: 'offline_precheck',
    title: 'Offline pre-check',
    explains: 'The locally cached policy engine evaluates first, so a machine with no network still enforces.',
  },
  {
    id: 'request',
    title: 'Control-plane request',
    explains: 'The real call to the runtime API, shown exactly as it goes over the wire.',
  },
  {
    id: 'shield',
    title: 'Shield pipeline',
    explains: 'Tier by tier, cheapest first, exiting as early as the evidence allows.',
  },
  {
    id: 'policy',
    title: 'Action policy',
    explains: 'Which policy, which rule and which constraint decided the outcome.',
  },
  {
    id: 'decision',
    title: 'Decision',
    explains: 'Allow, block, or hold for a second human.',
  },
  {
    id: 'approval',
    title: 'Approval',
    explains: 'The wait for a named approver, resolvable here or in the dashboard.',
  },
  {
    id: 'execution',
    title: 'Execution',
    explains: 'The tool call or shell command actually running on the target host.',
  },
  {
    id: 'response_scan',
    title: 'Response scan',
    explains: 'Tool output checked for injected instructions before it re-enters the model context.',
  },
  {
    id: 'evidence',
    title: 'Evidence',
    explains: 'The durable record, and where to find it in AgentGuard.',
  },
];
